import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { FaBook, FaFilePdf, FaPlayCircle, FaDownload } from 'react-icons/fa';
import { authFetch } from '../utils/auth';
import { downloadResource } from '../utils/resourceDownload';
import VideoPlayerModal from '../components/conversation/VideoPlayerModal';

// Thumbnail mapping based on resource blobPath
// Uses normalized comparison to handle Unicode differences (NFC vs NFD)
const getThumbnailUrl = (blobPath) => {
  if (!blobPath) return null;

  const thumbnailMap = {
    'intro til coord.mp4': '/images/resources/Velkommen-Thumbnail.png',
    'sjekklisten.pdf': '/images/resources/sjekklisten.png',
    'rundskriv til ftrl kap 6_ grunnstønad og hjelpestønad - lovdata.pdf': '/images/resources/rundskriv.png',
    'håndboken.pdf': '/images/resources/handboken.png',
    'når diagnosen er satt - rettigheter og muligheter ved autisme, adhd eller tourettes syndrom.pdf': '/images/resources/nar-diagnosen-er-satt.png',
    'i møte med systemet.pdf': '/images/resources/spiss-i-mote-med-systemet.png',
    'skoleoverganger.pdf': '/images/resources/spiss-skoleoverganger.png'
  };

  // Normalize and lowercase for comparison to handle Unicode differences
  const normalizedPath = blobPath.normalize('NFC').toLowerCase();
  return thumbnailMap[normalizedPath] || null;
};

const Resources = () => {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // Fetch resources from API
  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await authFetch('/resources');

        if (!response.ok) {
          throw new Error('Failed to fetch resources');
        }

        const data = await response.json();
        setResources(data);
      } catch (err) {
        console.error('Error fetching resources:', err);
        setError('Kunne ikke hente ressurser. Vennligst prøv igjen senere.');
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // Handle video play - dispatches event for VideoPlayerModal
  const handleVideoPlay = (resourceId) => {
    window.dispatchEvent(new CustomEvent('open-video-modal', {
      detail: { resourceId }
    }));
  };

  // Handle PDF download (shared with the sources panel in chat)
  const handleDownload = async (resourceId, resourceTitle) => {
    setDownloadingId(resourceId);
    try {
      await downloadResource(resourceId, resourceTitle);
    } finally {
      setDownloadingId(null);
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf':
      case 'guide':
        return <FaFilePdf className="w-6 h-6" />;
      case 'video':
        return <FaPlayCircle className="w-6 h-6" />;
      default:
        return <FaBook className="w-6 h-6" />;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-background-card rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-200 animate-pulse" style={{ aspectRatio: '3/2' }}></div>
              <div className="p-5">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-3 animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-error-light border border-error rounded-xl text-error-dark px-6 py-4">
          <strong className="font-bold">Feil: </strong>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (resources.length === 0) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{t('resources.title')}</h1>
          <p className="text-text-secondary">{t('resources.description')}</p>
        </div>

        <div className="bg-background-card rounded-xl p-8 text-center">
          <FaBook className="w-12 h-12 mx-auto text-text-muted mb-4" />
          <p className="text-text-secondary">Ingen ressurser tilgjengelig ennå.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t('resources.title')}</h1>
        <p className="text-text-secondary">{t('resources.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.map((resource) => (
          <div key={resource._id} className="bg-background-card rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
            <div className="relative bg-neutral-100 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '3/2' }}>
              {/* Thumbnail image or fallback icon */}
              {getThumbnailUrl(resource.blobPath) ? (
                <img
                  src={getThumbnailUrl(resource.blobPath)}
                  alt={resource.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="text-neutral-300">
                  {getResourceIcon(resource.type)}
                </div>
              )}

              {/* Sponsor badge - rendered for both thumbnail and icon fallback */}
              {resource.sponsorName && (
                <span className="absolute top-2 right-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-white/90 text-primary-800 rounded-full shadow-sm">
                  Sponset av {resource.sponsorName}
                </span>
              )}
            </div>

            <div className="p-5">
              <h3 className="font-semibold text-lg mb-2 line-clamp-2 text-text-primary">{resource.title}</h3>
              <p className="text-text-secondary text-sm mb-3 line-clamp-2">{resource.description}</p>

              {resource.benefitArea && (
                <div className="flex items-center text-sm text-text-muted mb-4">
                  <span>{resource.benefitArea}</span>
                </div>
              )}

              <div className="flex gap-2">
                {resource.type === 'video' ? (
                  <button
                    onClick={() => handleVideoPlay(resource._id)}
                    className="flex-1 bg-primary text-white rounded-lg py-2 px-4 flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors"
                  >
                    <FaPlayCircle className="w-5 h-5" />
                    Se video
                  </button>
                ) : (
                  <button
                    onClick={() => handleDownload(resource._id, resource.title)}
                    disabled={downloadingId === resource._id}
                    className="flex-1 bg-primary text-white rounded-lg py-2 px-4 flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors disabled:opacity-50"
                  >
                    {downloadingId === resource._id ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Laster...
                      </>
                    ) : (
                      <>
                        <FaDownload className="w-5 h-5" />
                        Last ned .pdf
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal />
    </div>
  );
};

export default Resources;
